// พื้นที่ของฉัน notification counters — the shared store behind the dot in the
// sidebar and the badges on the /me tabs.
//
// ONE store, module-level, exactly like ./chat.js. A badge showing 2 over a list
// showing 0 is the specific failure that makes people stop looking, so there is
// only one copy of the number and every consumer reads it.
//
// Backend: GET /api/me/notice → Xano GET /notice, which takes no input at all and
// resolves the employee from $auth.id — there is nothing here to scope wrongly.
//
// Deliberately NOT a notification bell. This is four numbers, not an inbox; the
// CRM already carries one inbox story (the patient chat) and a second one for
// "you have an unread warning" would be out of all proportion.
import { useEffect, useState } from 'react'
import { apiGet } from './api.js'

const EMPTY = {
  warnings_unack: 0,
  leave_decided: 0,
  resign_decided: 0,
  survey_pending: 0,
  total: 0,
}

let _notice = EMPTY
let _loaded = false
const _subs = new Set()
const _emit = () => { for (const fn of _subs) fn() }

const n = (v) => Number(v) || 0

export function setNotice(next) {
  if (!next || typeof next !== 'object') return
  _notice = {
    warnings_unack: n(next.warnings_unack),
    leave_decided: n(next.leave_decided),
    resign_decided: n(next.resign_decided),
    survey_pending: n(next.survey_pending),
    total: n(next.total),
  }
  _loaded = true
  _emit()
}

let _inflight = null

/**
 * Refresh the counters.
 *
 * 🔑 A FAILED POLL KEEPS THE PREVIOUS NUMBERS. "We don't know" must never render
 * as "nothing new" — an employee who is shown a cleared badge stops checking, and
 * an unacknowledged warning is exactly the thing that must not go quiet. Same rule
 * doctor_portal's lib/inbox.js and chat.js#refreshUnread both keep.
 *
 * Concurrent calls are collapsed: mount and window-focus fire together on a tab
 * switch, and that must be one request, not two.
 */
export async function refreshNotice() {
  if (_inflight) return _inflight
  _inflight = apiGet('/api/me/notice')
    .then((r) => { setNotice(r); return _notice })
    .catch(() => _notice)
    .finally(() => { _inflight = null })
  return _inflight
}

/**
 * Subscribe to the counters.
 *
 * `autoLoad` fetches on mount and refreshes on window focus. Pass it from ONE
 * place only — the sidebar, which is always mounted — or every badge on the page
 * kicks its own first request.
 */
export function useNotice({ autoLoad = false } = {}) {
  const [, force] = useState(0)
  useEffect(() => {
    const rerender = () => force((x) => x + 1)
    _subs.add(rerender)
    if (!autoLoad) return () => { _subs.delete(rerender) }

    refreshNotice()
    const onFocus = () => refreshNotice()
    window.addEventListener('focus', onFocus)
    return () => {
      _subs.delete(rerender)
      window.removeEventListener('focus', onFocus)
    }
  }, [autoLoad])
  return _notice
}

// Has anything ever loaded? Lets a badge stay absent (rather than render a
// confident 0) until the first answer arrives.
export const noticeLoaded = () => _loaded

/**
 * Drop a counter locally the moment the user acts on it, so the dot clears without
 * waiting for the next poll. The server is still the source of truth — the next
 * refresh overwrites this — but a badge that lingers after you have read the thing
 * teaches people the badge is lying.
 */
export function decNotice(key, by = 1) {
  if (!(key in _notice)) return
  const nextVal = Math.max(0, _notice[key] - by)
  const delta = _notice[key] - nextVal
  setNotice({ ..._notice, [key]: nextVal, total: Math.max(0, _notice.total - delta) })
}
